import { ReactElement } from 'react';
import dayjs from 'dayjs';
import { MILLISECONDS_IN_SECOND } from 'utils/constants/dateInfo/time-numbers';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { dateFormat } from 'utils/date-manipulation/date-format';
import { checkIsNotESportType } from 'utils/sportsbook/check-type-is-not-sportbook';
import { ETeamNumber } from 'interfaces/sportsbook';
import {
  Avatar,
  CompetitionName,
  Content,
  Flag,
  Header,
  MatchDetails,
  SportImageOverlay,
  StartDay,
  StartTime,
  StyledImg,
  TeamFlag,
  TeamName
} from './style';
import { MatchHeaderSkeleton } from 'components/MatchHeader/Prematch/MatchHeaderSkelton';
import { TeamLogo } from 'components/TeamLogo';
import { useSportImage } from 'hooks/sportsbook/useSportImage';
import { TGameDataWithMoreInfo } from 'store/reducers/new-sportsbook';
import './index.less';

type Props = {
  gameData: TGameDataWithMoreInfo;
};

export function MatchHeader({ gameData }: Props): ReactElement | null {
  const { competitionName, game, sportAlias } = gameData;
  const { team1_name, team2_name, start_ts, team1_id, team2_id } = game;

  const ts = (start_ts as number) * MILLISECONDS_IN_SECOND;
  const sportImageSrc = useSportImage(sportAlias);

  if (!gameData) {
    return <MatchHeaderSkeleton />;
  }

  return Object.keys(game).length ? (
    <Header>
      <SportImageOverlay />
      <StyledImg src={sportImageSrc} loading="lazy" />
      <Content>
        <TeamFlag className="teamFlag__container">
          <TeamLogo
            isHomeOrAway={ETeamNumber.HOME}
            isNotESport={checkIsNotESportType(game)}
            teamId={team1_id as number}
            size="lg"
            sizeByPixels={44}
          >
            <Flag>
              <Avatar>{String(team1_name).substr(0, 2).toUpperCase()}</Avatar>
            </Flag>
          </TeamLogo>
          <TeamName>{team1_name}</TeamName>
        </TeamFlag>

        <MatchDetails $isOneGame={!!team2_id}>
          <CompetitionName>{competitionName}</CompetitionName>
          <StartDay>
            {dayjs(ts).format(
              dateFormat({
                date: SpringConfigs.DT.longDate.replace(' YYYY', ''),
                includeWeekday: true
              })
            )}
          </StartDay>
          <StartTime>{dayjs(ts).format(SpringConfigs.DT.time)}</StartTime>
        </MatchDetails>
        {team2_id && (
          <TeamFlag className="teamFlag__container">
            <TeamLogo
              isHomeOrAway={ETeamNumber.AWAY}
              isNotESport={checkIsNotESportType(game)}
              teamId={team2_id as number}
              size="lg"
              sizeByPixels={44}
            >
              <Flag>
                <Avatar>{String(team2_name).substr(0, 2).toUpperCase()}</Avatar>
              </Flag>
            </TeamLogo>
            <TeamName>{team2_name}</TeamName>
          </TeamFlag>
        )}
      </Content>
    </Header>
  ) : null;
}
